import * as React from "react"
import PropTypes from "prop-types"
import HeroSectionWrapper from "components/Hero/HeroSectionWrapper"
import HeroWrapper from "components/Hero/HeroWrapper"
import HeroContainer from "components/Hero/HeroContainer"
import Breadcrumbs from "components/Breadcrumbs"
import HeroBreadcrumbsLink from "components/Hero/HeroBreadcrumbsLink"
import HeroText from "components/Hero/HeroText"
import HeroTextBeforeTitle from "components/Hero/HeroTextBeforeTitle"
import HeroTitle from "components/Hero/HeroTitle"
import HeroPersonName from "components/Hero/HeroPersonName"
import HeroImage from "components/Hero/HeroImage"
import HeroImageContainer from "components/Hero/HeroImageContainer"
import HeroBackgroundImage from "components/Hero/HeroBackgroundImage"
import HeroArticle from "components/Hero/HeroArticle"
import HeroTextsWrapper from "components/Hero/HeroTextsWrapper"
import Link from "components/Link"
import htmlFormatting from "utils/htmlFormatting"
import Button from "components/Buttons/Button"
import Meta from "components/Meta"
import ModalForm from "components/ModalForm/ModalForm"

const Hero = ({
  theme,
  breadcrumbs,
  textBeforeTitle,
  textBeforeTitleTag,
  addQuotes,
  title,
  titleTag,
  personName,
  heroText,
  buttonText,
  buttonLink,
  sectionScroll,
  backgroundImage,
  image,
  imageSize,
  rightText,
  buttonLinkClassName,
  hasModal,
  formSubmitLink,
  formFields,
  formTexts,
}) => {
  const isBrowser = typeof window !== "undefined"
  const [isModalOpen, setModalOpen] = React.useState(false)

  const handleButtonClick = () => {
    if (hasModal && formSubmitLink) {
      setModalOpen(true)
    } else if (buttonLink) {
      window.location.href = buttonLink
    }
  }

  const handleScroll = () => {
    document.querySelector("#gatsby-focus-wrapper").style.overflow = "inherit"

    const el = document.getElementById(sectionScroll)
    const y = el.getBoundingClientRect().top + (isBrowser ? window.scrollY : 0) - 100

    if (isBrowser) {
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  const destructuredFormTexts = formTexts?.[0] || {}
  return (
    <HeroSectionWrapper>
      <HeroWrapper colorVariant={theme} backgroundImage={backgroundImage}>
        <HeroContainer imgAndText={image !== null && rightText !== null}>
          {breadcrumbs?.length && (
            <Breadcrumbs itemsCope="" itemType="http://schema.org/BreadcrumbList">
              {breadcrumbs.map(
                (el, idx) =>
                  (breadcrumbs.length - 1 !== idx && (
                    <li
                      itemProp="itemListElement"
                      itemsCope=""
                      itemType="https://schema.org/ListItem"
                    >
                      <HeroBreadcrumbsLink
                        colorVariant={theme}
                        itemProp="item"
                        isActive={breadcrumbs.length - 1 === idx}
                        to={el.link}
                        key={el.id}
                      >
                        <span itemProp="name">{el.title}</span>
                        <Meta itemProp="position" content={idx + 1} />
                      </HeroBreadcrumbsLink>
                    </li>
                  )) || (
                    <HeroBreadcrumbsLink
                      key={el.id}
                      colorVariant={theme}
                      itemsCope=""
                      itemProp="itemListElement"
                      itemType="https://schema.org/ListItem"
                      isActive={breadcrumbs.length - 1 === idx}
                    >
                      <span itemProp="name">{el.title}</span>
                      <Meta itemProp="position" content={idx + 1} />
                    </HeroBreadcrumbsLink>
                  ),
              )}
            </Breadcrumbs>
          )}
          {textBeforeTitle && (
            <HeroTextBeforeTitle colorVariant={theme} as={textBeforeTitleTag}>
              {textBeforeTitle}
            </HeroTextBeforeTitle>
          )}
          <HeroTitle
            backgroundImage={backgroundImage}
            addQuotes={addQuotes}
            image={backgroundImage === "None" && !!image}
            colorVariant={theme}
            as={titleTag}
          >
            {(addQuotes ? "“‎‎" : "") + title + (addQuotes ? "”‎" : "")}
          </HeroTitle>
          {personName && <HeroPersonName colorVariant={theme}>{personName}</HeroPersonName>}
          <HeroTextsWrapper imgAndText={image !== null && rightText !== null}>
            {heroText && (
              <HeroText
                colorVariant={theme}
                backgroundImage={backgroundImage}
                rightText={rightText}
                addQuotes={addQuotes}
                dangerouslySetInnerHTML={{
                  __html: htmlFormatting(heroText?.childMarkdownRemark?.html),
                }}
                imgAndText={image !== null && rightText !== null}
                imageExist={image !== null}
              />
            )}
            {rightText && (
              <HeroArticle
                imgAndText={!!image}
                colorVariant={theme}
                dangerouslySetInnerHTML={{
                  __html: htmlFormatting(rightText?.childMarkdownRemark?.html),
                }}
              />
            )}
          </HeroTextsWrapper>
          <HeroImageContainer
            imageExist={image !== null}
            backgroundImage={backgroundImage}
            addQuotes={addQuotes}
          >
            <HeroImage
              imageSize={imageSize}
              {...image}
              backgroundImage={backgroundImage}
              objectFit="contain"
              alt={image?.description || image?.alt || ""}
            />
            {backgroundImage !== "None" && (
              <HeroBackgroundImage colorVariant={theme} backgroundImage={backgroundImage} />
            )}
          </HeroImageContainer>

          {buttonText && !sectionScroll && (
            <Button
              as={!hasModal ? Link : Button}
              className={buttonLinkClassName}
              to={buttonLink}
              variant="contained"
              onClick={hasModal && formSubmitLink && handleButtonClick}
            >
              {buttonText}
            </Button>
          )}
          {buttonText && sectionScroll && (
            <Button onClick={handleScroll} variant="contained">
              {buttonText}
            </Button>
          )}
        </HeroContainer>
      </HeroWrapper>
      {isModalOpen && hasModal && formSubmitLink && (
        <ModalForm
          formFields={formFields}
          formTexts={destructuredFormTexts}
          formSubmitLink={formSubmitLink}
          onClose={() => setModalOpen(false)}
        />
      )}
    </HeroSectionWrapper>
  )
}

Hero.propTypes = {
  theme: PropTypes.string.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.exact({
      id: PropTypes.string,
      title: PropTypes.string,
      link: PropTypes.string,
    }),
  ),
  textBeforeTitle: PropTypes.string,
  textBeforeTitleTag: PropTypes.string.isRequired,
  addQuotes: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  titleTag: PropTypes.string.isRequired,
  personName: PropTypes.string.isRequired,
  heroText: PropTypes.object.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonLink: PropTypes.string.isRequired,
  sectionScroll: PropTypes.string,
  backgroundImage: PropTypes.string.isRequired,
  image: PropTypes.object,
  imageSize: PropTypes.string.isRequired,
  rightText: PropTypes.object,
  buttonLinkClassName: PropTypes.string,
  hasModal: PropTypes.bool,
  formSubmitLink: PropTypes.string,
  formFields: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      type: PropTypes.string,
      required: PropTypes.bool,
    }),
  ),
  formTexts: PropTypes.arrayOf(
    PropTypes.shape({
      close: PropTypes.string.isRequired,
      closeSuccess: PropTypes.string.isRequired,
      subTitle: PropTypes.string,
      successMessage: PropTypes.string,
      submit: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
}

Hero.defaultProps = {
  breadcrumbs: [],
  textBeforeTitle: "",
  image: null,
  rightText: null,
  sectionScroll: "",
  buttonLinkClassName: "",
  hasModal: false,
  formSubmitLink: "",
  formFields: [],
  formTexts: [],
}

export default Hero
